import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  CircularProgress,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { templateService } from "../services/jobService";
import JdAttachmentField from "./JdAttachmentField";

// Blue palette (consistent with other modules)
const BLUE = {
  50: "#eff6ff",
  100: "#dbeafe",
  200: "#bfdbfe",
  300: "#93c5fd",
  400: "#60a5fa",
  500: "#3b82f6",
  600: "#2563eb",
  700: "#1d4ed8",
  800: "#1e40af",
  900: "#1e3a8a",
};

// Keyframes for subtle animations
if (
  typeof document !== "undefined" &&
  !document.getElementById("template-form-keyframes")
) {
  const style = document.createElement("style");
  style.id = "template-form-keyframes";
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    @keyframes fadeSlideUp {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";

const TemplateFormModal = ({
  open,
  onClose,
  template,
  store_id,
  user_id,
  onSaved,
  // Optional. Set when the template is being created from inside a department's
  // hierarchy, so it is born attached to that level instead of landing in the
  // library unassigned. Both undefined from the template library, which is why
  // the existing call site needs no change.
  departmentId,
  nodeId,
  contextLabel,
}) => {
  const [formData, setFormData] = useState({
    template_name: "",
    title: "",
    description: "",
    responsibilities: "",
    required_skills: "",
    experience_required: "",
    salary_min: "",
    salary_max: "",
    status: "ACTIVE",
    questions: [],
  });
  // The JD document, as { url, name } or null. Held apart from formData
  // because it is not a text field the generic change handler can serve, and
  // because it is already uploaded by the time it lands here — JdAttachmentField
  // exchanges the file for a URL on pick, so what this holds is just a value.
  const [jd, setJd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (template) {
      setFormData({
        template_name: template.template_name || "",
        title: template.title || "",
        description: template.description || "",
        responsibilities: template.responsibilities || "",
        required_skills: template.required_skills || "",
        experience_required: template.experience_required || "",
        salary_min: template.salary_min || "",
        salary_max: template.salary_max || "",
        status: template.status || "ACTIVE",
        questions: template.questions ? [...template.questions] : [],
      });
      setJd(
        template.jd_pdf_url
          ? { url: template.jd_pdf_url, name: template.jd_pdf_name }
          : null,
      );
    } else {
      setFormData({
        template_name: "",
        title: "",
        description: "",
        responsibilities: "",
        required_skills: "",
        experience_required: "",
        salary_min: "",
        salary_max: "",
        status: "ACTIVE",
        questions: [],
      });
      // Cleared with the rest of the form. Left behind, the next "Create New
      // Template" would open pre-attached to the last template's JD.
      setJd(null);
    }
  }, [template, open]);

  const handleFieldChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [
        ...formData.questions,
        {
          question: "",
          answer_options: ["", "", "", "", ""],
          option_scores: [1, 2, 3, 4, 5],
          question_type: "TEXT",
          is_required: true,
          expected_answer: "",
        },
      ],
    });
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...formData.questions];
    updated[index][field] = value;
    setFormData({ ...formData, questions: updated });
  };

  const updateAnswerOption = (qIndex, optionIndex, value) => {
    const updated = [...formData.questions];
    const options = [
      ...(updated[qIndex].answer_options || ["", "", "", "", ""]),
    ];
    options[optionIndex] = value;
    updated[qIndex].answer_options = options;
    if (
      !updated[qIndex].option_scores ||
      updated[qIndex].option_scores.length !== 5
    ) {
      updated[qIndex].option_scores = [1, 2, 3, 4, 5];
    }
    setFormData({ ...formData, questions: updated });
  };

  const removeQuestion = (index) => {
    const updated = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: updated });
  };

  const validate = () => {
    if (!formData.template_name.trim()) return "Template name is required";
    if (!formData.title.trim()) return "Job title is required";
    if (formData.questions.length === 0)
      return "At least one screening question is required";
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (!q.question.trim()) return `Question ${i + 1} is empty`;
      if (q.answer_options && q.answer_options.length === 5) {
        for (let j = 0; j < 5; j++) {
          if (!q.answer_options[j]?.trim()) {
            return `Question ${i + 1}: Score ${j + 1} answer is empty`;
          }
        }
      } else {
        if (!q.max_score || q.max_score <= 0) {
          return `Question ${i + 1} max score must be >0`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const errMsg = validate();
    if (errMsg) {
      setError(errMsg);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const questionsToSend = formData.questions.map((q) => {
        if (q.answer_options && q.answer_options.length === 5) {
          const sanitizedOptions = q.answer_options.map((opt) => opt.trim());
          return {
            question: q.question,
            answer_options: sanitizedOptions,
            option_scores: [1, 2, 3, 4, 5],
            question_type: q.question_type,
            is_required: q.is_required,
            expected_answer: "",
            max_score: 5,
          };
        } else {
          return {
            question: q.question,
            expected_answer: q.expected_answer || "",
            max_score: q.max_score || 5,
            question_type: q.question_type,
            is_required: q.is_required,
            answer_options: null,
            option_scores: null,
          };
        }
      });

      const payload = {
        store_id,
        created_by: user_id,
        // Only sent when this modal was opened from a level. Omitted entirely
        // otherwise, so a library template stays unassigned rather than being
        // stamped with nulls it did not ask for.
        ...(departmentId ? { department_id: departmentId } : {}),
        ...(nodeId ? { dept_node_id: nodeId } : {}),
        template_name: formData.template_name,
        title: formData.title,
        description: formData.description,
        responsibilities: formData.responsibilities,
        required_skills: formData.required_skills,
        experience_required: formData.experience_required,
        salary_min: Number(formData.salary_min) || null,
        salary_max: Number(formData.salary_max) || null,
        // Null rather than omitted when there is no document, so removing a JD
        // from a template actually clears the column. Omitting the key would
        // leave the old URL in place and the form would appear to have saved a
        // removal it did not.
        jd_pdf_url: jd?.url || null,
        jd_pdf_name: jd?.name || null,
        status: formData.status,
        questions: questionsToSend,
      };
      if (template) {
        await templateService.updateTemplate(template.id, payload);
      } else {
        await templateService.createTemplate(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.statusMessage || "Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "28px",
          boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          height: 6,
          background: `linear-gradient(90deg, #0c8a9b 0%, #0c8a9b 100%)`,
        }}
      />
      <DialogTitle sx={{ pt: 3, pb: 1, px: 3, fontFamily: fontFam }}>
        <Typography variant="h5" fontWeight={800} sx={{ color: "#0f172a" }}>
          {template ? "Edit Template" : "Create New Template"}
        </Typography>
        {/* Only when opened from a level, so the author can see the template is
            being attached rather than added to the flat library. */}
        {contextLabel && (
          <Typography
            sx={{
              fontFamily: fontFam,
              fontSize: 13,
              color: BLUE[600],
              mt: 0.5,
            }}
          >
            On {contextLabel}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "16px" }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Template Name"
              name="template_name"
              value={formData.template_name}
              onChange={handleFieldChange}
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Job Title"
              name="title"
              value={formData.title}
              onChange={handleFieldChange}
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFieldChange}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Responsibilities"
              name="responsibilities"
              value={formData.responsibilities}
              onChange={handleFieldChange}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Required Skills"
              name="required_skills"
              value={formData.required_skills}
              onChange={handleFieldChange}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Experience Required"
              name="experience_required"
              value={formData.experience_required}
              onChange={handleFieldChange}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleFieldChange}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label="Min Salary"
              name="salary_min"
              value={formData.salary_min}
              onChange={handleFieldChange}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label="Max Salary"
              name="salary_max"
              value={formData.salary_max}
              onChange={handleFieldChange}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
            />
          </Grid>

          {/* The JD document. Authored here once; every job opened from this
              template starts with it attached, and each job keeps its own copy
              from then on — so editing this template later cannot change the
              PDF a candidate was already sent. */}
          <Grid item xs={12}>
            <JdAttachmentField
              value={jd}
              onChange={setJd}
              helperText="Jobs created from this template start with this PDF attached, and it is emailed to candidates with their interview link."
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: BLUE[100] }} />
        <Typography
          variant="h6"
          fontWeight={700}
          gutterBottom
          sx={{ color: "#0f242a" }}
        >
          Screening Questions
        </Typography>

        {formData.questions.map((q, idx) => (
          <Paper
            key={idx}
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: "20px",
              border: `1px solid #0c8a9b`,
              backgroundColor: "#ffffff",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: BLUE[300],
                boxShadow: `0 4px 12px #0c8a9b, 0.08)}`,
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton onClick={() => removeQuestion(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Question"
                  value={q.question}
                  onChange={(e) =>
                    updateQuestion(idx, "question", e.target.value)
                  }
                  required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#475569" }}
                >
                  Answer Options (Select the one that matches candidate's
                  response)
                </Typography>
              </Grid>
              {[1, 2, 3, 4, 5].map((scoreValue) => (
                <Grid item xs={12} key={scoreValue}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Score ${scoreValue} Answer`}
                    value={q.answer_options?.[scoreValue - 1] || ""}
                    onChange={(e) =>
                      updateAnswerOption(idx, scoreValue - 1, e.target.value)
                    }
                    placeholder={`Answer that would give ${scoreValue} point(s)`}
                    helperText={`Candidate receives ${scoreValue} point if this matches`}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                    }}
                  />
                </Grid>
              ))}
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Question Type"
                  value={q.question_type}
                  onChange={(e) =>
                    updateQuestion(idx, "question_type", e.target.value)
                  }
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                >
                  <MenuItem value="TEXT">Text</MenuItem>
                  <MenuItem value="PARAGRAPH">Paragraph</MenuItem>
                  <MenuItem value="MULTIPLE_CHOICE">Multiple Choice</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={q.is_required}
                      onChange={(e) =>
                        updateQuestion(idx, "is_required", e.target.checked)
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#0c8a9b",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#0c8a9b",
                          },
                      }}
                    />
                  }
                  label="Required"
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={addQuestion}
          variant="outlined"
          size="small"
          sx={{
            borderRadius: "40px",
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#0c8a9b",
            color: "#0c8a9b",
            "&:hover": {
              borderColor: "#0c8a9b",
              backgroundColor: "#0c8a9b7a",
            },
          }}
        >
          Add Question
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "40px",
            textTransform: "none",
            fontFamily: fontFam,
            fontWeight: 600,
            color: "#64748b",
            "&:hover": { backgroundColor: "#f1f5f9" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: "40px",
            textTransform: "none",
            fontWeight: 600,
            color: "#fff",
            background: `linear-gradient(135deg, #0c8a9b 0%, #0c8a9b 100%)`,
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: `0 6px 14px #0c8a9b, 0.3)}`,
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : template ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateFormModal;
